import { Container, Graphics } from 'pixi.js';
import { cosmeticById, type EquippedCosmetics } from '@duck/contracts';
import { isReducedMotion } from './reduced-motion';

/**
 * Procedural cosmetics (M6 "Express Yourself" — emotion: Identity), drawn with Pixi primitives ONLY
 * (no image/texture assets, per ADR-0010). Each catalog cosmetic carries a `style` key; we switch on
 * it to build a small Graphics that sits on the duck. This is the SAME look for self AND others — the
 * engine reads each player's synced `cosmetics` and drives {@link DuckCosmetics.apply}.
 *
 * Geometry note (kept in lock-step with `duck.ts`): the duck's body is an ellipse at (0,0) r≈(22,18);
 * the head is a circle at (14,-14) r=12 (drawn facing RIGHT). Hats/glasses/outfits live INSIDE the
 * duck's `body` container so they flip with `body.scale.x` and bob with the walk — they're authored in
 * the head/body-local space below. The companion PET is the one exception: it follows on the floor in
 * the duck's `root` space with its own gentle easing (see {@link PetFollower}).
 *
 * Cheap + pooled: a slot's Graphics is built once per style and reused; re-equipping the same style is
 * a no-op, and switching styles rebuilds only that one slot. Nothing allocates per frame except the
 * pet's lerp (scalar maths). Cosmetic-only — never affects any capability (Constitution Art. 1).
 */

const GOLD = 0xffd23f;
const DARK = 0x2a2f3a;
const WHITE = 0xf4f4f8;

/**
 * Outfit-slot styles that are full-body SKINS (they cover the whole body) rather than garments worn over
 * it. Skins are routed to {@link DuckCosmetics.skinLayer} — mounted by the Critter UNDER the face overlay
 * — so they can reskin the head + torso while the eyes/snout stay legible on top (Wave 4, finding S6).
 */
const SKIN_STYLES = new Set(['galaxy', 'neon', 'robot', 'gold']);

/** Build the static Graphics for a HAT style, anchored to sit on the head crown (head ≈ (14,-14)). */
function buildHat(style: string): Graphics {
  const g = new Graphics();
  // Head crown is ~ (14, -26). We draw around that point.
  const cx = 14;
  switch (style) {
    case 'beanie':
      // A snug knit cap hugging the crown, with a folded brim.
      g.ellipse(cx, -24, 13, 9).fill(0xe05a5a);
      g.roundRect(cx - 13, -22, 26, 5, 2.5).fill(0xc94545);
      // A tiny pom-pom.
      g.circle(cx, -33, 3).fill(WHITE);
      break;
    case 'party':
      // A cheerful cone with a pom on top.
      g.poly([cx, -42, cx - 10, -22, cx + 10, -22]).fill(0x4dd0e1);
      g.poly([cx - 5, -32, cx + 5, -32, cx, -22]).fill({ color: WHITE, alpha: 0.5 });
      g.circle(cx, -42, 3).fill(0xff7ab6);
      break;
    case 'tophat':
      // A dapper top hat: brim + tall crown + band.
      g.ellipse(cx, -23, 15, 4).fill(DARK);
      g.roundRect(cx - 10, -42, 20, 20, 2).fill(0x1c2129);
      g.roundRect(cx - 10, -28, 20, 4, 1).fill(0xb5793a);
      break;
    case 'crown':
      // A golden crown: a band with three points + jewels. Earned, never bought your way to.
      g.poly([
        cx - 13, -22,
        cx - 13, -32,
        cx - 7, -26,
        cx, -36,
        cx + 7, -26,
        cx + 13, -32,
        cx + 13, -22,
      ]).fill(GOLD);
      g.circle(cx, -30, 1.8).fill(0xff5d8f);
      g.circle(cx - 9, -28, 1.4).fill(0x5ad1ff);
      g.circle(cx + 9, -28, 1.4).fill(0x5ad1ff);
      break;
    case 'cowboy':
      // A curved-brim cowboy hat with a tan crown + band. Howdy.
      g.ellipse(cx, -22, 17, 4).fill(0x9c6b3f);
      g.ellipse(cx - 12, -22, 4, 2.5).fill(0xb07d4d); // up-turned brim edge
      g.ellipse(cx + 12, -22, 4, 2.5).fill(0xb07d4d);
      g.roundRect(cx - 9, -36, 18, 14, 5).fill(0xb07d4d);
      g.roundRect(cx - 9, -27, 18, 3, 1.5).fill(0x5e3d22); // band
      break;
    case 'propeller':
      // A beanie cap with a little spinning propeller on top.
      g.ellipse(cx, -23, 12, 8).fill(0x4f7cff);
      g.roundRect(cx - 12, -22, 24, 4, 2).fill(0xffd23f); // brim stripe
      g.moveTo(cx, -31).lineTo(cx, -36).stroke({ color: 0x2a2f3a, width: 1.4 });
      g.ellipse(cx - 7, -37, 6, 2).fill(0xff5d8f); // propeller blades
      g.ellipse(cx + 7, -37, 6, 2).fill(0x5ad1ff);
      g.circle(cx, -37, 1.6).fill(0x2a2f3a);
      break;
    case 'chef':
      // A puffy white chef's toque with a banded base.
      g.roundRect(cx - 11, -29, 22, 7, 2).fill(WHITE); // band
      g.circle(cx - 7, -34, 6).fill(WHITE);
      g.circle(cx, -36, 7).fill(WHITE);
      g.circle(cx + 7, -34, 6).fill(WHITE);
      g.roundRect(cx - 11, -27, 22, 2, 1).fill({ color: 0xd8d8e0, alpha: 0.7 });
      break;
    case 'wizard':
      // A tall, slightly-leaning starry wizard hat with a curved tip + brim.
      g.ellipse(cx, -22, 15, 4).fill(0x3a2d6b);
      g.poly([cx - 11, -23, cx + 11, -23, cx + 6, -48]).fill(0x4b3a8c);
      g.circle(cx + 6, -48, 2).fill(0x4b3a8c); // curled tip
      g.poly([cx + 2, -36, cx + 4, -33, cx + 7, -33, cx + 5, -30, cx + 6, -27, cx + 3, -29, cx, -27, cx + 1, -30, cx - 1, -33, cx + 2, -33]).fill(GOLD); // star
      g.circle(cx - 3, -28, 0.9).fill(WHITE);
      g.circle(cx + 4, -42, 0.9).fill(WHITE);
      break;
    case 'headphones':
      // Over-ear studio cans: a band arcing over the crown + an earcup by the head.
      g.moveTo(cx - 12, -22).bezierCurveTo(cx - 12, -36, cx + 12, -36, cx + 12, -22).stroke({ color: DARK, width: 3 });
      g.roundRect(cx - 14, -24, 5, 9, 2.5).fill(0x16181f); // far earcup
      g.roundRect(cx + 9, -24, 5, 9, 2.5).fill(0x2a2f3a); // near earcup
      g.roundRect(cx + 10, -22, 3, 5, 1.5).fill(0x5ad1ff); // a glowing accent
      break;
    case 'flower':
      // A ring of little blossoms around the crown — bloom where you waddle.
      for (const [ox, oy, col] of [
        [cx - 12, -24, 0xff7ab6],
        [cx - 6, -29, 0xffd23f],
        [cx + 1, -31, 0xff5d8f],
        [cx + 8, -29, 0xffe08a],
        [cx + 13, -24, 0xff9ecb],
      ] as const) {
        for (let i = 0; i < 5; i++) {
          const a = (i / 5) * Math.PI * 2;
          g.circle(ox + Math.cos(a) * 2.4, oy + Math.sin(a) * 2.4, 1.7).fill(col);
        }
        g.circle(ox, oy, 1.4).fill(0xfff3c4);
      }
      break;
    case 'pirate':
      // A black bicorne pirate hat with a skull. Yarr.
      g.ellipse(cx, -25, 16, 6).fill(0x1c2129);
      g.poly([cx - 16, -25, cx, -38, cx + 16, -25, cx, -29]).fill(0x2a2f3a);
      g.circle(cx, -30, 2.4).fill(WHITE); // skull
      g.circle(cx - 0.8, -30.5, 0.6).fill(0x1c2129);
      g.circle(cx + 0.8, -30.5, 0.6).fill(0x1c2129);
      g.rect(cx - 2.2, -27.5, 4.4, 1.2).fill(WHITE); // crossbones hint
      break;
    case 'halo':
      // A floating golden halo hovering above the crown — for the kindest of ducks.
      g.ellipse(cx, -38, 12, 4).stroke({ color: GOLD, width: 3 });
      g.ellipse(cx, -38, 12, 4).stroke({ color: 0xfff3c4, width: 1, alpha: 0.8 });
      g.ellipse(cx, -38, 14, 5).stroke({ color: GOLD, width: 1, alpha: 0.25 }); // soft glow
      break;
    default:
      break;
  }
  return g;
}

/** Build the static Graphics for a GLASSES style, anchored over the eye area (eye ≈ (18,-16)). */
function buildGlasses(style: string): Graphics {
  const g = new Graphics();
  switch (style) {
    case 'round':
      // Two round lenses + a bridge — studious and sweet.
      g.circle(18, -15, 4.5).stroke({ color: DARK, width: 1.6 }).fill({ color: 0xbfe8ff, alpha: 0.35 });
      g.circle(9, -15, 4.5).stroke({ color: DARK, width: 1.6 }).fill({ color: 0xbfe8ff, alpha: 0.35 });
      g.rect(13, -16, 1.5, 1.6).fill(DARK); // bridge
      break;
    case 'shades':
      // Cool, permanently-unbothered dark shades.
      g.roundRect(13.5, -19, 9, 6, 2).fill(0x16181f);
      g.roundRect(4, -19, 9, 6, 2).fill(0x16181f);
      g.rect(13, -17, 1.5, 1.5).fill(0x16181f);
      // A glint of light.
      g.circle(20, -17.5, 1).fill({ color: WHITE, alpha: 0.6 });
      break;
    case 'heart':
      // Two little heart lenses — love the world.
      for (const ox of [18, 9]) {
        g.poly([
          ox, -12,
          ox - 4, -16,
          ox - 4, -18,
          ox - 2, -19,
          ox, -17,
          ox + 2, -19,
          ox + 4, -18,
          ox + 4, -16,
        ]).fill({ color: 0xff5d8f, alpha: 0.85 });
      }
      break;
    case 'monocle':
      // A single gold-rimmed lens over the near eye, with a dangling chain. Distinguished.
      g.circle(18, -15, 5).stroke({ color: GOLD, width: 1.8 }).fill({ color: 0xffffff, alpha: 0.18 });
      g.circle(20, -17, 1.1).fill({ color: WHITE, alpha: 0.6 }); // glint
      g.moveTo(16, -10).bezierCurveTo(13, -6, 11, -2, 10, 2).stroke({ color: GOLD, width: 1 }); // chain
      break;
    case '3d':
      // Retro cyan/red 3D specs.
      g.roundRect(13.5, -19, 9, 6, 1.5).fill({ color: 0xff3b50, alpha: 0.6 });
      g.roundRect(4, -19, 9, 6, 1.5).fill({ color: 0x2bd1ff, alpha: 0.6 });
      g.rect(13, -17, 1.5, 1.5).fill(DARK);
      g.roundRect(13.5, -19, 9, 6, 1.5).stroke({ color: WHITE, width: 1 });
      g.roundRect(4, -19, 9, 6, 1.5).stroke({ color: WHITE, width: 1 });
      break;
    case 'goggles':
      // Wide ski goggles: a single tinted band with a strap. Ready for the downhill sprint.
      g.roundRect(3, -20, 20, 9, 4.5).fill(0x2a2f3a);
      g.roundRect(5, -19, 16, 6, 3).fill({ color: 0x5ad1ff, alpha: 0.5 });
      g.roundRect(5, -19, 16, 2, 1).fill({ color: WHITE, alpha: 0.35 }); // reflection
      g.rect(0, -17, 4, 2.5).fill(0xff5d8f); // strap side
      break;
    case 'star':
      // Two star-shaped shades — a little stardust on your face.
      for (const ox of [18, 9]) {
        const pts: number[] = [];
        for (let i = 0; i < 10; i++) {
          const a = -Math.PI / 2 + (i / 10) * Math.PI * 2;
          const r = i % 2 === 0 ? 5 : 2.2;
          pts.push(ox + Math.cos(a) * r, -15 + Math.sin(a) * r);
        }
        g.poly(pts).fill(0x16181f);
      }
      g.circle(20, -16, 0.9).fill({ color: GOLD, alpha: 0.8 }); // sparkle
      break;
    case 'nerd':
      // Chunky square specs with a taped bridge. Big brain energy.
      g.roundRect(13.5, -19, 9, 8, 1.5).stroke({ color: 0x16181f, width: 2 }).fill({ color: 0xbfe8ff, alpha: 0.25 });
      g.roundRect(4, -19, 9, 8, 1.5).stroke({ color: 0x16181f, width: 2 }).fill({ color: 0xbfe8ff, alpha: 0.25 });
      g.rect(12.8, -16.5, 1.9, 3.5).fill(WHITE); // tape on the bridge
      break;
    default:
      break;
  }
  return g;
}

/** Build the static Graphics for an OUTFIT style, sitting on the neck/body. */
function buildOutfit(style: string): Graphics {
  const g = new Graphics();
  switch (style) {
    case 'scarf':
      // A friendly scarf around the neck + a dangling tail.
      g.ellipse(7, 0, 16, 5).fill(0xe06d3a);
      g.roundRect(-2, 2, 6, 14, 3).fill(0xc95a2b);
      g.roundRect(-2, 9, 6, 4, 2).fill(0xe06d3a);
      break;
    case 'hoodie':
      // A guild hoodie: a coloured torso panel + a hood lump behind the neck + drawstrings.
      g.ellipse(0, 6, 20, 13).fill(0x4f7cff);
      g.ellipse(2, 4, 14, 9).fill({ color: 0x3a63d8, alpha: 0.6 });
      g.ellipse(8, -6, 9, 6).fill(0x3a63d8); // hood behind the head
      g.roundRect(2, 0, 1.5, 7, 0.75).fill(WHITE);
      g.roundRect(6, 0, 1.5, 7, 0.75).fill(WHITE);
      break;
    case 'cape':
      // A hero cape flowing behind — for shipping the raid boss.
      g.poly([-2, -8, -22, 4, -16, 22, -2, 16]).fill(0x9b2c5d);
      g.poly([-2, -8, -16, 0, -10, 14, -2, 10]).fill({ color: 0xff5d8f, alpha: 0.35 });
      // A clasp at the neck.
      g.circle(0, -7, 2.2).fill(GOLD);
      break;
    case 'tuxedo':
      // A black tux torso with a white shirt front, lapels + a bow tie. Very fancy retros.
      g.ellipse(0, 6, 20, 13).fill(0x1c2129);
      g.poly([-4, -4, 4, -4, 2, 16, -2, 16]).fill(WHITE); // shirt front
      g.poly([-4, -4, 0, 2, -8, 6]).fill(0x10131a); // left lapel
      g.poly([4, -4, 0, 2, 8, 6]).fill(0x10131a); // right lapel
      g.poly([-3.5, -5, 0, -3, -3.5, -1]).fill(DARK); // bow tie, left wing
      g.poly([3.5, -5, 0, -3, 3.5, -1]).fill(DARK); // bow tie, right wing
      g.circle(0, 4, 1).fill(0x2a2f3a); // button
      g.circle(0, 9, 1).fill(0x2a2f3a);
      break;
    case 'hivis':
      // A high-visibility safety vest with reflective stripes. Under construction, in the best way.
      g.ellipse(0, 6, 20, 13).fill(0xffe08a);
      g.ellipse(0, 6, 20, 13).fill({ color: 0xffaa00, alpha: 0.55 });
      g.roundRect(-12, 1, 4, 14, 1).fill({ color: 0xf4f4f8, alpha: 0.9 }); // vertical stripe
      g.roundRect(8, 1, 4, 14, 1).fill({ color: 0xf4f4f8, alpha: 0.9 });
      g.roundRect(-12, 6, 24, 3, 1).fill({ color: 0xf4f4f8, alpha: 0.9 }); // horizontal stripe
      break;
    case 'overalls':
      // Denim overalls: a torso panel with bib + two straps + buttons. Roll up your (non-)sleeves.
      g.ellipse(0, 8, 19, 11).fill(0x4a6fa5);
      g.roundRect(-7, -2, 14, 12, 2).fill(0x3c5d8a); // bib
      g.roundRect(-6, -8, 2.5, 9, 1).fill(0x3c5d8a); // strap
      g.roundRect(3.5, -8, 2.5, 9, 1).fill(0x3c5d8a); // strap
      g.circle(-5, 0, 1.2).fill(GOLD); // buttons
      g.circle(4.5, 0, 1.2).fill(GOLD);
      g.roundRect(-3, 2, 6, 5, 1).fill({ color: 0x33517a, alpha: 0.8 }); // pocket
      break;
    case 'labcoat':
      // An open white lab coat with a collar, pocket + a pen. For your finest experiments.
      g.ellipse(0, 7, 20, 13).fill(WHITE);
      g.poly([-2, -6, -10, 2, -7, 4]).fill(0xe6e6ee); // left lapel
      g.poly([2, -6, 10, 2, 7, 4]).fill(0xe6e6ee); // right lapel
      g.moveTo(0, -4).lineTo(0, 18).stroke({ color: 0xd0d0da, width: 1 }); // center seam
      g.roundRect(-11, 4, 6, 5, 1).fill(0xeef0f6).stroke({ color: 0xd0d0da, width: 0.8 }); // pocket
      g.roundRect(-9, 2, 1.2, 5, 0.6).fill(0x5ad1ff); // pen
      break;
    case 'raincoat':
      // A glossy yellow raincoat with a hood + buttons. Stay cozy through any storm.
      g.ellipse(0, 7, 20, 13).fill(0xffd23f);
      g.ellipse(0, 6, 20, 13).fill({ color: 0xffe08a, alpha: 0.4 });
      g.ellipse(8, -6, 9, 6).fill(0xf0bf2a); // hood behind the head
      g.moveTo(0, -4).lineTo(0, 18).stroke({ color: 0xd9a400, width: 1 }); // zip
      g.circle(0, 2, 1).fill(0xb98a00);
      g.circle(0, 8, 1).fill(0xb98a00);
      g.circle(0, 14, 1).fill(0xb98a00);
      break;
    // ── Full-body "skin" overlays (cover the body ellipse, body-local r≈(22,18)). ──
    case 'galaxy':
      // A deep-space overlay with stars dusted across the body. A whole cosmos in one duck.
      g.ellipse(0, 0, 23, 19).fill(0x1b1245);
      g.circle(14, -14, 13).fill(0x1b1245); // head dome (Wave 4 S6 — the face re-stamp lands on top)
      g.circle(10, -16, 0.8).fill(WHITE);
      g.circle(19, -12, 0.7).fill(WHITE); // a couple of stars dusted onto the head
      g.ellipse(-4, -2, 14, 12).fill({ color: 0x5a3fb0, alpha: 0.5 }); // nebula
      g.ellipse(6, 4, 10, 8).fill({ color: 0xb03f8c, alpha: 0.35 });
      for (const [sx, sy, r] of [
        [-12, -6, 1.1], [-3, 6, 0.8], [8, -7, 1.2], [13, 3, 0.9],
        [2, -3, 0.7], [-8, 8, 0.9], [16, -2, 0.8], [-15, 2, 0.7],
      ] as const) {
        g.circle(sx, sy, r).fill(WHITE);
      }
      g.poly([4, -10, 5, -8, 7, -8, 5.5, -6.5, 6, -4.5, 4, -6, 2, -4.5, 2.5, -6.5, 1, -8, 3, -8]).fill(GOLD); // a bright star
      break;
    case 'neon':
      // A cyber-neon overlay with glowing circuit lines. Glow with good vibes.
      g.ellipse(0, 0, 23, 19).fill(0x10142a);
      g.circle(14, -14, 13).fill(0x10142a); // head dome (Wave 4 S6)
      g.moveTo(7, -16).lineTo(20, -16).stroke({ color: 0x2bffd1, width: 1.2 }); // a circuit trace on the head
      g.circle(20, -16, 1.3).fill(0x5ad1ff);
      g.moveTo(-18, -2).lineTo(-6, -2).lineTo(-2, 4).lineTo(8, 4).stroke({ color: 0x2bffd1, width: 1.6 });
      g.moveTo(-12, 8).lineTo(0, 8).lineTo(4, 2).lineTo(16, 2).stroke({ color: 0xff3bd1, width: 1.6 });
      g.moveTo(-14, -8).lineTo(2, -8).lineTo(6, -3).stroke({ color: 0x5ad1ff, width: 1.4 });
      for (const [nx, ny, col] of [
        [-6, -2, 0x2bffd1], [8, 4, 0x2bffd1], [0, 8, 0xff3bd1], [16, 2, 0xff3bd1], [2, -8, 0x5ad1ff],
      ] as const) {
        g.circle(nx, ny, 1.6).fill(col);
      }
      break;
    case 'robot':
      // A metallic robot-shell overlay with panel lines, rivets + a chest light. A duck of the future.
      g.ellipse(0, 0, 23, 19).fill(0x9aa3b2);
      g.circle(14, -14, 13).fill(0x9aa3b2); // head dome (Wave 4 S6)
      g.ellipse(10, -17, 6, 4).fill({ color: 0xc2c9d4, alpha: 0.6 }); // head shell highlight
      g.ellipse(-5, -4, 13, 9).fill({ color: 0xc2c9d4, alpha: 0.7 }); // highlight
      g.moveTo(-22, 2).lineTo(22, 2).stroke({ color: 0x6b7382, width: 1 }); // panel seam
      g.moveTo(0, -14).lineTo(0, 16).stroke({ color: 0x6b7382, width: 1 });
      for (const [rx, ry] of [[-14, -6], [12, -6], [-14, 8], [12, 8]] as const) {
        g.circle(rx, ry, 1.2).fill(0x5b6270); // rivets
      }
      g.roundRect(-4, -2, 8, 6, 1).fill(0x2a2f3a); // chest panel
      g.circle(0, 1, 1.8).fill(0x5ad1ff); // power light
      break;
    case 'gold':
      // A shimmering solid-gold sheen with a bright highlight + sparkle. Solid gold, pure heart.
      g.ellipse(0, 0, 23, 19).fill(0xe8a900);
      g.circle(14, -14, 13).fill(0xe8a900); // head dome (Wave 4 S6)
      g.ellipse(11, -17, 5, 3).fill({ color: 0xfff3c4, alpha: 0.7 }); // head sheen
      g.ellipse(-3, -3, 16, 13).fill({ color: GOLD, alpha: 0.85 });
      g.ellipse(-7, -6, 8, 5).fill({ color: 0xfff3c4, alpha: 0.8 }); // bright sheen
      g.ellipse(8, 7, 9, 6).fill({ color: 0xb98a00, alpha: 0.5 }); // shadow side
      g.poly([10, -8, 11, -6, 13, -6, 11.5, -4.5, 12, -2.5, 10, -4, 8, -2.5, 8.5, -4.5, 7, -6, 9, -6]).fill(WHITE); // sparkle
      break;
    default:
      break;
  }
  return g;
}

/**
 * The companion PET (M6) that follows the duck with gentle easing. Lives in the duck's `root` space
 * (NOT inside `body`, so it stays upright on the floor and doesn't flip/bob with the duck), trailing
 * to a target offset BEHIND the duck based on facing. Drawn procedurally by style. Reduced-motion
 * aware: it still FOLLOWS (the position ease is the point of a companion), but its idle bounce is
 * flattened so there's no looping motion for motion-sensitive players.
 */
export class PetFollower {
  readonly root = new Container();
  private style: string;
  private phase = 0;
  /** Eased local position (in the duck's root space). Starts behind/under the duck. */
  private px = -26;
  private py = 16;

  constructor(style: string) {
    this.style = style;
    this.root.addChild(buildPet(style));
    this.root.x = this.px;
    this.root.y = this.py;
  }

  /** Rebuild for a new style (re-equip). No-op if unchanged. */
  setStyle(style: string): void {
    if (this.style === style) return;
    this.style = style;
    this.root.removeChildren().forEach((c) => c.destroy());
    this.root.addChild(buildPet(style));
  }

  /**
   * Ease the pet toward a trailing point behind the duck. `facing` (radians) places the target on the
   * duck's BACK so the companion waddles after them; `dtMs` drives the frame-rate-independent ease and
   * the gentle idle bob (skipped under reduced motion). All scalar maths — no per-frame allocation.
   */
  update(facing: number, dtMs: number): void {
    // Trail point: a short distance opposite the facing direction, slightly to the side + on the floor.
    const tx = -Math.cos(facing) * 24;
    const ty = -Math.sin(facing) * 10 + 16;
    // Frame-rate-independent exponential ease toward the trail point (gentle lag → "follows you").
    const k = 1 - Math.exp(-dtMs * 0.006);
    this.px += (tx - this.px) * k;
    this.py += (ty - this.py) * k;
    this.root.x = this.px;
    let bob = 0;
    if (!isReducedMotion()) {
      this.phase += dtMs * 0.006;
      bob = Math.abs(Math.sin(this.phase)) * 2.5; // a tiny hop, never under reduced motion
    }
    this.root.y = this.py - bob;
  }

  destroy(): void {
    this.root.destroy({ children: true });
  }
}

/** Build the static Graphics for a PET style (small, ~16px, sits on the floor facing right). */
function buildPet(style: string): Graphics {
  const g = new Graphics();
  switch (style) {
    case 'duckling':
      // A tiny yellow duckling — body, head, beak.
      g.ellipse(0, 22, 7, 3).fill({ color: 0x000000, alpha: 0.2 }); // shadow
      g.ellipse(0, 12, 7, 6).fill(GOLD);
      g.circle(4, 5, 4).fill(GOLD);
      g.circle(5.5, 4, 0.9).fill(0x141821);
      g.poly([7.5, 5, 11, 6, 7.5, 7]).fill(0xff9f1c);
      break;
    case 'cat':
      // An office cat — it tolerates you. Mostly.
      g.ellipse(0, 22, 8, 3).fill({ color: 0x000000, alpha: 0.2 });
      g.ellipse(-1, 13, 8, 6).fill(0x8a8f9c);
      g.circle(5, 6, 4.5).fill(0x8a8f9c);
      g.poly([2, 3, 4, -2, 6, 3]).fill(0x8a8f9c); // ear
      g.poly([6, 3, 8, -2, 9.5, 3]).fill(0x8a8f9c); // ear
      g.circle(6.5, 6, 0.9).fill(0x2bd17a); // a green eye
      g.moveTo(-8, 10).lineTo(-12, 4).stroke({ color: 0x8a8f9c, width: 2 }); // tail
      break;
    case 'bot':
      // A helper bot — beep, boop, belonging.
      g.ellipse(0, 22, 8, 3).fill({ color: 0x000000, alpha: 0.2 });
      g.roundRect(-6, 6, 12, 12, 3).fill(0x5ad1ff);
      g.roundRect(-4, 9, 8, 4, 1).fill(0x102030); // visor
      g.circle(-1, 11, 1).fill(0x9bf0ff);
      g.circle(2, 11, 1).fill(0x9bf0ff);
      g.moveTo(0, 6).lineTo(0, 1).stroke({ color: 0x9bf0ff, width: 1.4 }); // antenna
      g.circle(0, 0.5, 1.6).fill(0xff5d8f);
      break;
    case 'crab':
      // A little red crab — round shell, two eye-stalks, claws + legs. Skitters sideways, loyal forever.
      g.ellipse(0, 22, 8, 3).fill({ color: 0x000000, alpha: 0.2 });
      g.ellipse(0, 14, 8, 6).fill(0xe0533a);
      g.ellipse(0, 12, 6, 3).fill({ color: 0xff7a5c, alpha: 0.6 }); // shell sheen
      g.moveTo(-3, 9).lineTo(-3, 5).stroke({ color: 0xe0533a, width: 1.4 }); // eye stalks
      g.moveTo(3, 9).lineTo(3, 5).stroke({ color: 0xe0533a, width: 1.4 });
      g.circle(-3, 4.5, 1.3).fill(WHITE);
      g.circle(3, 4.5, 1.3).fill(WHITE);
      g.circle(-3, 4.5, 0.6).fill(0x141821);
      g.circle(3, 4.5, 0.6).fill(0x141821);
      g.poly([-8, 13, -12, 10, -10, 14, -12, 14]).fill(0xe0533a); // left claw
      g.poly([8, 13, 12, 10, 10, 14, 12, 14]).fill(0xe0533a); // right claw
      for (const lx of [-6, 6]) {
        g.moveTo(lx, 17).lineTo(lx + Math.sign(lx) * 3, 20).stroke({ color: 0xe0533a, width: 1.2 });
      }
      break;
    case 'frog':
      // A cheerful green frog — squat body, two bulging eyes, a wide smile. Ribbits encouragement.
      g.ellipse(0, 22, 8, 3).fill({ color: 0x000000, alpha: 0.2 });
      g.ellipse(0, 15, 8, 6).fill(0x4caf50);
      g.ellipse(0, 13, 6, 3).fill({ color: 0x6fd275, alpha: 0.5 }); // belly light
      g.circle(-3.5, 7, 3).fill(0x4caf50); // eye mounds
      g.circle(3.5, 7, 3).fill(0x4caf50);
      g.circle(-3.5, 6.5, 1.6).fill(WHITE);
      g.circle(3.5, 6.5, 1.6).fill(WHITE);
      g.circle(-3.5, 6.5, 0.8).fill(0x141821);
      g.circle(3.5, 6.5, 0.8).fill(0x141821);
      g.moveTo(-4, 14).bezierCurveTo(-2, 17, 2, 17, 4, 14).stroke({ color: 0x2e7d32, width: 1.2 }); // smile
      g.ellipse(-7, 19, 3, 1.6).fill(0x4caf50); // feet
      g.ellipse(7, 19, 3, 1.6).fill(0x4caf50);
      break;
    case 'butterfly':
      // A delicate butterfly — two pairs of wings, a slim body, antennae. Flutters wherever you go.
      g.ellipse(0, 20, 6, 2).fill({ color: 0x000000, alpha: 0.15 });
      g.ellipse(-5, 7, 5, 6).fill({ color: 0xff7ab6, alpha: 0.9 }); // upper-left wing
      g.ellipse(5, 7, 5, 6).fill({ color: 0xff7ab6, alpha: 0.9 }); // upper-right wing
      g.ellipse(-4, 13, 4, 4).fill({ color: 0x5ad1ff, alpha: 0.9 }); // lower-left wing
      g.ellipse(4, 13, 4, 4).fill({ color: 0x5ad1ff, alpha: 0.9 }); // lower-right wing
      g.circle(-5, 7, 1.4).fill({ color: WHITE, alpha: 0.7 }); // wing spots
      g.circle(5, 7, 1.4).fill({ color: WHITE, alpha: 0.7 });
      g.roundRect(-1, 4, 2, 13, 1).fill(0x2a2f3a); // body
      g.moveTo(-0.5, 4).lineTo(-3, 0).stroke({ color: 0x2a2f3a, width: 1 }); // antennae
      g.moveTo(0.5, 4).lineTo(3, 0).stroke({ color: 0x2a2f3a, width: 1 });
      g.circle(-3, 0, 0.9).fill(0x2a2f3a);
      g.circle(3, 0, 0.9).fill(0x2a2f3a);
      break;
    case 'ghost':
      // A friendly translucent ghost — rounded top, wavy hem, big gentle eyes. A very gentle haunting.
      g.ellipse(0, 22, 7, 2.5).fill({ color: 0x000000, alpha: 0.12 });
      g.poly([
        -7, 18, -7, 8,
        -7, 2, 7, 2, 7, 8,
        7, 18, 4, 15, 1.5, 18, -1.5, 15, -4, 18,
      ]).fill({ color: WHITE, alpha: 0.85 });
      g.ellipse(0, 6, 7, 6).fill({ color: WHITE, alpha: 0.85 }); // rounded head dome
      g.circle(-2.5, 7, 1.4).fill(0x3a3f4a);
      g.circle(2.5, 7, 1.4).fill(0x3a3f4a);
      g.ellipse(0, 11, 2, 1.4).fill({ color: 0xff9ecb, alpha: 0.6 }); // little mouth
      break;
    default:
      break;
  }
  return g;
}

/**
 * Manages the full equipped LOOK on one duck. Owns the head/body-attached cosmetic layers (hat /
 * glasses / outfit) and the floor-level pet follower. The owning {@link Duck} mounts `bodyLayer`
 * INSIDE its `body` (so worn items flip + bob with the duck) and `petLayer` in its `root` (so the
 * companion stays upright on the floor). Pooled + idempotent: {@link apply} only rebuilds a slot whose
 * style actually changed.
 */
export class DuckCosmetics {
  /** Hat/glasses/garment-outfit live here — mounted inside the duck's `body` OVER the critter by the owner. */
  readonly bodyLayer = new Container();
  /** Full-body SKIN overlay — mounted inside `body` BETWEEN the species body + face by the owner, so a
   *  skin reskins the head/torso while the eyes/snout stay on top (Wave 4 S6). Holds at most one skin. */
  readonly skinLayer = new Container();
  /** The companion pet lives here — mounted in the duck's `root` by the owner (floor-level). */
  readonly petLayer = new Container();

  // Stable sub-layers so draw order is fixed regardless of equip order: outfit BEHIND, then glasses,
  // then the hat on top. Each holds at most one Graphics; re-equipping swaps that child.
  private readonly outfitLayer = new Container();
  private readonly glassesLayer = new Container();
  private readonly hatLayer = new Container();

  // The style each slot currently shows (so re-applying the same set is a cheap no-op).
  private hatStyle = '';
  private glassesStyle = '';
  private outfitStyle = '';

  private pet: PetFollower | null = null;
  private petStyle = '';

  constructor() {
    // Back-to-front: outfit (cape/hood) behind, glasses over the face, hat on top of everything.
    this.bodyLayer.addChild(this.outfitLayer, this.glassesLayer, this.hatLayer);
  }

  /**
   * Apply an equipped set (the synced look). `id`s are catalog cosmetic ids (or '' / undefined for an
   * empty slot — the realtime encodes unset slots as ''). Resolves each id → its `style`, then rebuilds
   * only the slots whose style changed. Safe to call every frame (cheap + idempotent).
   */
  apply(equipped: EquippedCosmetics): void {
    this.setWorn('hat', equipped.hat);
    this.setWorn('glasses', equipped.glasses);
    this.setOutfit(equipped.outfit);
    this.setPet(equipped.pet);
  }

  /** Resolve a cosmetic id to its render `style`, or '' when the slot is empty/unknown. */
  private styleOf(id: string | undefined): string {
    if (!id) return '';
    return cosmeticById(id)?.style ?? '';
  }

  private setWorn(slot: 'hat' | 'glasses', id: string | undefined): void {
    const style = this.styleOf(id);
    const current = slot === 'hat' ? this.hatStyle : this.glassesStyle;
    if (style === current) return;

    const layer = slot === 'hat' ? this.hatLayer : this.glassesLayer;
    // Tear down the old Graphics for this slot (each layer holds at most one).
    layer.removeChildren().forEach((c) => c.destroy());

    if (style) layer.addChild(slot === 'hat' ? buildHat(style) : buildGlasses(style));

    if (slot === 'hat') this.hatStyle = style;
    else this.glassesStyle = style;
  }

  /**
   * Apply the OUTFIT slot. A full-body SKIN (galaxy/neon/robot/gold) goes in {@link skinLayer} (mounted
   * by the Critter UNDER the face, so it reskins the head/torso while the eyes/snout stay on top); any
   * other garment (scarf/hoodie/tux/…) goes in the over-body {@link outfitLayer}. The slot holds one item,
   * so a change clears BOTH homes before rebuilding. Cheap + idempotent (no-op when the style is unchanged).
   */
  private setOutfit(id: string | undefined): void {
    const style = this.styleOf(id);
    if (style === this.outfitStyle) return;
    this.outfitLayer.removeChildren().forEach((c) => c.destroy());
    this.skinLayer.removeChildren().forEach((c) => c.destroy());
    if (style) {
      const g = buildOutfit(style);
      (SKIN_STYLES.has(style) ? this.skinLayer : this.outfitLayer).addChild(g);
    }
    this.outfitStyle = style;
  }

  private setPet(id: string | undefined): void {
    const style = this.styleOf(id);
    if (style === this.petStyle) return;
    this.petStyle = style;
    if (!style) {
      if (this.pet) {
        this.petLayer.removeChild(this.pet.root);
        this.pet.destroy();
        this.pet = null;
      }
      return;
    }
    if (this.pet) {
      this.pet.setStyle(style);
    } else {
      this.pet = new PetFollower(style);
      this.petLayer.addChild(this.pet.root);
    }
  }

  /** Drive the pet's follow + idle motion (no-op when no pet is equipped). */
  updatePet(facing: number, dtMs: number): void {
    this.pet?.update(facing, dtMs);
  }

  destroy(): void {
    this.pet?.destroy();
    this.bodyLayer.destroy({ children: true });
    this.petLayer.destroy({ children: true });
  }
}
